import { Fragment, useState, useMemo, useCallback } from 'react';
import { Box, Divider } from '@mui/material';
import { useRouter } from '@tanstack/react-router';
import { QUERIES_KEYS } from 'constant';
import { useApplicationData } from 'remote-state/applicationHooks';
import { useFeatureFlagQuery } from 'remote-state/featureFlagsHooks';
import { FEATURE_FLAGS_KEYS } from 'constants/featureFlags';
import { datadogRum } from '@datadog/browser-rum';
import { DATADOG_TRACK_EVENTS } from 'common/utils/eventMapping';
import { useUserInfo } from 'remote-state/userServiceHooks';

import useTexts from '../useTexts';
import navIcons from '../navIcons';
import NavItemLink from './navItem/navItemLink';
import NavItemUser from './navItem/navItemUser';
import NavItemMenu from './navItem/navItemMenu';
import NavItemCustom from './navItem/navItemCustom';
import StyledNavList from './style';

const saasItem = {
  id: 'licenseManager',
  rbToken: 'spaces.license-manager',
  subMenuList: [],
  isOpenInNewTab: false,
  url: '/spaces/license-manager',
  type: 'custom',
};

export default function NavList({ expanded }) {
  const { data: navElements } = useApplicationData(QUERIES_KEYS.MENU_ITEMS);
  const { getNavItemTitle } = useTexts();
  const [openItemId, setOpenItemId] = useState(null);
  const router = useRouter();
  //
  const { data: userData } = useUserInfo(QUERIES_KEYS.CURRENT_USER_FROM_CORE);
  const isSysAidAdmin = userData?.isSysAidAdmin;

  const { data: isLicenseManagerEnabled } = useFeatureFlagQuery({
    flagKey: FEATURE_FLAGS_KEYS.LICENSE_MANAGER,
    defaultValue: false,
  });
  const handleToggleMenu = (id) => {
    setOpenItemId((prevOpenItemId) => (prevOpenItemId === id ? null : id));
  };

  const handleToggleLicenseManagerMenu = useCallback(() => {
    datadogRum.addAction(DATADOG_TRACK_EVENTS.LICENSE_MANAGER_MENU_ITEM_CLICKED);
    router.navigate({ to: '/spaces/license-manager' });
  }, [router]);

  //Adds the license manager item to the menu
  const fullNavElements = useMemo(() => {
    if (!isLicenseManagerEnabled || !isSysAidAdmin) {
      return navElements;
    }

    const insertLicenseManagerItem = (menuItems) => {
      const firstTwoItems = menuItems.slice(0, 2);
      const remainingItems = menuItems.slice(2);
      const licenseManagerItem = {
        ...saasItem,
        customHandleToggleMenu: handleToggleLicenseManagerMenu,
      };

      return [...firstTwoItems, licenseManagerItem, ...remainingItems];
    };

    return {
      ...navElements,
      menu: insertLicenseManagerItem(navElements.menu),
    };
  }, [isLicenseManagerEnabled, navElements, handleToggleLicenseManagerMenu, isSysAidAdmin]);

  return (
    <Box sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper', paddingBottom: '30px' }}>
      {fullNavElements &&
        Object.entries(fullNavElements).map(([section, navItems], sectionIndex) => (
          <Fragment key={sectionIndex}>
            {!!sectionIndex && <Divider />}
            <nav>
              <StyledNavList className={section} expanded={expanded}>
                {navItems?.map(({ id, url, rbToken, isOpenInNewTab, subMenuList, type, customHandleToggleMenu }) => {
                  const title = getNavItemTitle(rbToken);
                  const icon = navIcons[id];
                  const { NavItem } = {
                    link: {
                      NavItem: NavItemLink,
                      title,
                      icon,
                    },
                    user: {
                      NavItem: NavItemUser,
                    },
                    menu: {
                      // prevents displaying an item that would open to an empty menu
                      NavItem: subMenuList.every(({ type, subMenuList }) => type === 'menu' && subMenuList.length === 0)
                        ? null
                        : NavItemMenu,
                      title,
                      icon,
                    },
                    custom: {
                      NavItem: NavItemCustom,
                      title: getNavItemTitle(rbToken),
                      icon: navIcons[id],
                    },
                  }[type];

                  const isOpen =
                    type === 'custom' && id === saasItem.id
                      ? router.state.location.pathname.includes(url)
                      : openItemId === id;

                  return (
                    NavItem && (
                      <NavItem
                        key={id}
                        id={id}
                        title={title}
                        icon={icon}
                        menu={subMenuList}
                        url={url}
                        isOpenInNewTab={isOpenInNewTab}
                        expanded={expanded}
                        isOpen={isOpen}
                        onToggle={customHandleToggleMenu ?? handleToggleMenu}
                      />
                    )
                  );
                })}
              </StyledNavList>
            </nav>
          </Fragment>
        ))}
    </Box>
  );
}
